import { apiClient } from './client';

export interface Organization {
  id: string;
  name: string;
  description?: string;
  custom_domain?: string | null;
  settings: Record<string, any>;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  stats: {
    member_count: number;
    survey_count: number;
    response_count: number;
  };
}

export interface OrganizationByHost {
  id: string;
  name: string;
  description?: string | null;
  custom_domain: string;
}

export interface OrganizationMember {
  id: string;
  user: {
    id: string;
    email: string;
    full_name: string;
  };
  role: 'OWNER' | 'ADMIN' | 'RESPONDENT';
  joined_at: string;
}

export interface CreateOrganizationData {
  name: string;
  description?: string;
  settings?: Record<string, any>;
}

export interface InviteMemberData {
  email: string;
  role: 'ADMIN' | 'RESPONDENT';
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
}

export interface OrganizationSurvey {
  id: string;
  title: string;
  status: string;
  is_active: boolean;
  questions?: { id: string }[];
  created_at: string;
  updated_at: string;
}

class OrganizationsAPI {
  async create(data: CreateOrganizationData): Promise<Organization> {
    const response = await apiClient.post('/organizations', data);
    return response.data;
  }

  async getMyOrganizations(): Promise<Organization[]> {
    const response = await apiClient.get('/organizations');
    return response.data;
  }

  async getOrganization(id: string): Promise<Organization> {
    const response = await apiClient.get(`/organizations/${id}`);
    return response.data;
  }

  async update(id: string, data: Partial<CreateOrganizationData>): Promise<Organization> {
    const response = await apiClient.put(`/organizations/${id}`, data);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/organizations/${id}`);
  }

  async getMembers(orgId: string): Promise<OrganizationMember[]> {
    const response = await apiClient.get(`/organizations/${orgId}/members`);
    return response.data;
  }

  async inviteMember(orgId: string, data: InviteMemberData): Promise<any> {
    const response = await apiClient.post(`/organizations/${orgId}/members/invite`, null, {
      params: data
    });
    return response.data;
  }

  async removeMember(orgId: string, memberId: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}/members/${memberId}`);
  }

  async updateMemberRole(orgId: string, memberId: string, role: string): Promise<any> {
    const response = await apiClient.put(`/organizations/${orgId}/members/${memberId}/role`, null, {
      params: { role }
    });
    return response.data;
  }

  async getInvitations(orgId: string): Promise<OrganizationInvitation[]> {
    const response = await apiClient.get(`/organizations/${orgId}/invitations`);
    return response.data;
  }

  async cancelInvitation(orgId: string, invitationId: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}/invitations/${invitationId}`);
  }

  async resendInvitation(orgId: string, invitation: OrganizationInvitation): Promise<OrganizationInvitation> {
    await this.cancelInvitation(orgId, invitation.id);
    return this.inviteMember(orgId, {
      email: invitation.email,
      role: invitation.role as 'ADMIN' | 'RESPONDENT',
    });
  }

  async getSurveys(orgId: string): Promise<OrganizationSurvey[]> {
    const response = await apiClient.get(`/surveys`, {
      params: { organization_id: orgId },
    });
    return response.data;
  }

  /**
   * Public lookup: which org owns the current request's hostname?
   * Returns null on 404 (no tenant for this host) so callers can branch
   * cleanly without inspecting axios errors.
   */
  async getByHost(): Promise<OrganizationByHost | null> {
    try {
      const response = await apiClient.get('/organizations/by-host');
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  /** Set or clear the org's custom domain. Pass null to clear. OWNER only. */
  async setCustomDomain(orgId: string, domain: string | null): Promise<Organization> {
    const response = await apiClient.put(
      `/organizations/${orgId}/custom-domain`,
      { custom_domain: domain },
    );
    return response.data;
  }
}

export const organizationsAPI = new OrganizationsAPI();